import React,{useRef,useEffect,useState,useMemo} from 'react';
import classNames from 'classnames';

/**
 * RichTextInput - A rich text editor component with formatting controls
 * Supports: bold, italic, bullet lists, and font size adjustment
 */
export default function RichTextInput({
    value='',
    onChange,
    placeholder='Enter text...',
    className='',
    showToolbar=true,
    readOnly=false,
    fontSize='medium' // 'small', 'medium', 'large', 'xlarge'
}) {
    const editorRef=useRef(null);
    const [isFocused,setIsFocused]=useState(false);
    const [currentFormat,setCurrentFormat]=useState({
        bold: false,
        italic: false,
        fontSize: fontSize
    });

    // Font size mappings
    const fontSizeMap=useMemo(() => ({
        small: '12px',
        medium: '14px',
        large: '18px',
        xlarge: '24px'
    }),[]);

    useEffect(() => {
        if(editorRef.current&&value!==editorRef.current.innerHTML) {
            editorRef.current.innerHTML=value||'';
        }
    },[value]);

    useEffect(() => {
        if(editorRef.current) {
            editorRef.current.style.fontSize=fontSizeMap[currentFormat.fontSize]||fontSizeMap.medium;
        }
    },[currentFormat.fontSize,fontSizeMap]);

    const handleInput=(e) => {
        if(onChange&&!readOnly) {
            onChange(e.currentTarget.innerHTML);
        }
    };

    const handleKeyDown=(e) => {
        // Prevent default for Ctrl/Cmd+B (bold) and Ctrl/Cmd+I (italic)
        if((e.metaKey||e.ctrlKey)) {
            if(e.key==='b') {
                e.preventDefault();
                applyFormat('bold');
            } else if(e.key==='i') {
                e.preventDefault();
                applyFormat('italic');
            }
        }
    };

    const applyFormat=(format) => {
        if(readOnly) return;

        switch(format) {
            case 'bold':
                document.execCommand('bold',false,null);
                setCurrentFormat(prev => ({...prev,bold: !prev.bold}));
                break;
            case 'italic':
                document.execCommand('italic',false,null);
                setCurrentFormat(prev => ({...prev,italic: !prev.italic}));
                break;
            case 'insertUnorderedList':
                document.execCommand('insertUnorderedList',false,null);
                break;
            default:
                break;
        }
        editorRef.current?.focus();
    };

    const changeFontSize=(size) => {
        if(readOnly) return;
        setCurrentFormat(prev => ({...prev,fontSize: size}));
        if(onChange&&editorRef.current) {
            onChange(editorRef.current.innerHTML);
        }
    };

    return (
        <div className={classNames('relative',className)}>
            {showToolbar&&isFocused&&!readOnly&&(
                <div className='absolute -top-10 left-0 flex gap-1 p-1 bg-white border rounded-md shadow-lg z-10'>
                    <button
                        type='button'
                        onClick={() => changeFontSize('small')}
                        className={classNames(
                            'px-2 py-1 text-xs rounded hover:bg-gray-100',
                            currentFormat.fontSize==='small'&&'bg-gray-200'
                        )}
                        title='Small'
                    >
                        S
                    </button>
                    <button
                        type='button'
                        onClick={() => changeFontSize('medium')}
                        className={classNames(
                            'px-2 py-1 text-sm rounded hover:bg-gray-100',
                            currentFormat.fontSize==='medium'&&'bg-gray-200'
                        )}
                        title='Medium'
                    >
                        M
                    </button>
                    <button
                        type='button'
                        onClick={() => changeFontSize('large')}
                        className={classNames(
                            'px-2 py-1 text-base rounded hover:bg-gray-100',
                            currentFormat.fontSize==='large'&&'bg-gray-200'
                        )}
                        title='Large'
                    >
                        L
                    </button>
                    <button
                        type='button'
                        onClick={() => changeFontSize('xlarge')}
                        className={classNames(
                            'px-2 py-1 text-lg rounded hover:bg-gray-100',
                            currentFormat.fontSize==='xlarge'&&'bg-gray-200'
                        )}
                        title='Extra Large'
                    >
                        XL
                    </button>
                    <div className='w-px bg-gray-300 mx-1' />
                    <button
                        type='button'
                        onClick={() => applyFormat('bold')}
                        className={classNames(
                            'px-2 py-1 text-sm font-bold rounded hover:bg-gray-100',
                            currentFormat.bold&&'bg-gray-200'
                        )}
                        title='Bold (Ctrl/Cmd+B)'
                    >
                        B
                    </button>
                    <button
                        type='button'
                        onClick={() => applyFormat('italic')}
                        className={classNames(
                            'px-2 py-1 text-sm italic rounded hover:bg-gray-100',
                            currentFormat.italic&&'bg-gray-200'
                        )}
                        title='Italic (Ctrl/Cmd+I)'
                    >
                        I
                    </button>
                    <button
                        type='button'
                        onClick={() => applyFormat('insertUnorderedList')}
                        className='px-2 py-1 text-sm rounded hover:bg-gray-100'
                        title='Bullet List'
                    >
                        •
                    </button>
                </div>
            )}
            <div
                ref={editorRef}
                contentEditable={!readOnly}
                onInput={handleInput}
                onKeyDown={handleKeyDown}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setTimeout(() => setIsFocused(false),200)}
                className={classNames(
                    'w-full p-2 bg-transparent focus:outline-none',
                    readOnly&&'cursor-default',
                    !value&&!readOnly&&'empty:before:content-[attr(data-placeholder)] empty:before:text-gray-400'
                )}
                data-placeholder={placeholder}
                style={{
                    minHeight: '1.5em',
                    fontSize: fontSizeMap[currentFormat.fontSize]||fontSizeMap.medium,
                    whiteSpace: 'pre-wrap',
                    wordWrap: 'break-word'
                }}
            />
        </div>
    );
}
